/**
 * MERCHANT PORTAL (Phase 5). Accept payments (QR, link, request), see takings, and settle to a bank or
 * PSP. Preview: settlement rails activate in a later sandbox phase; the flows and the numbers are shown
 * here without moving money.
 */

import { useState } from 'react';
import { fmtTime } from '@/lib/format';
import { QRCodeSVG } from 'qrcode.react';
import { BRAND_PINE, WHITE } from '@/lib/brand-colors';
import { api } from '@/lib/api';
import { ErrorState, LoadingCard } from '@/components/states';
import { useResource } from '@/lib/use-resource';
import { Reveal } from '@/components/motion';
import { Sheet } from '@/components/overlay';
import SettlementPanel from '@/components/SettlementPanel';

export default function Merchant() {
  const { data, loading, error, reload } = useResource(() => api.merchant(), []);
  const [qrOpen, setQrOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);

  if (loading)
    return (
      <div className="grid gap-6 lg:grid-cols-3 lg:items-start">
        <div className="space-y-6 lg:col-span-2">
          <LoadingCard lines={3} />
          <LoadingCard lines={3} />
        </div>
        <div className="space-y-6">
          <LoadingCard lines={2} />
        </div>
      </div>
    );
  if (error) return <ErrorState body="We could not load the merchant portal." onRetry={reload} />;
  if (!data) return null;

  return (
    <div className="grid gap-6 lg:grid-cols-3 lg:items-start">
      <div className="space-y-6 lg:col-span-2">
        <header className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-page-title text-ink">{data.profile.name}</h1>
            <p className="mt-0.5 text-sm text-ink-muted">Till {data.profile.till}</p>
          </div>
          <span className="rounded-pill bg-accent/15 px-2.5 py-0.5 text-micro font-medium capitalize text-accent">
            {data.profile.status}
          </span>
        </header>

        {/* TODAY hero + accept actions */}
        <Reveal>
          <section className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary to-primary-dark p-5 text-white shadow-lifted">
            <div
              aria-hidden
              className="pointer-events-none absolute -right-10 -top-12 h-44 w-44 rounded-full bg-primary-light/40 blur-2xl"
            />
            <div className="relative">
              <p className="text-sm text-white/70">Received today</p>
              <p className="mt-1 flex items-baseline gap-2">
                <span className="text-sm font-medium text-white/70">KES</span>
                <span className="tabular text-4xl font-semibold tracking-tight">
                  {data.today.received}
                </span>
              </p>
              <p className="mt-1 text-sm text-white/70">
                {data.today.count} payments &middot; avg KES {data.today.avg}
              </p>

              <div className="mt-5 grid grid-cols-3 gap-2">
                <button
                  onClick={() => setQrOpen(true)}
                  className="flex flex-col items-center gap-1.5 rounded-lg bg-white/10 py-2.5 text-white backdrop-blur transition hover:bg-white/20"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.8}
                      d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h3m3 0v3m0 3h-6v-3"
                    />
                  </svg>
                  <span className="text-micro font-medium">Show QR</span>
                </button>
                <button
                  onClick={() => setLinkOpen(true)}
                  className="flex flex-col items-center gap-1.5 rounded-lg bg-white/10 py-2.5 text-white backdrop-blur transition hover:bg-white/20"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.8}
                      d="M8.7 10.7 15.3 7m0 10-6.6-3.7M18 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm0 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM8 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z"
                    />
                  </svg>
                  <span className="text-micro font-medium">New link</span>
                </button>
                <button
                  onClick={() => setLinkOpen(true)}
                  className="flex flex-col items-center gap-1.5 rounded-lg bg-white/10 py-2.5 text-white backdrop-blur transition hover:bg-white/20"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.8}
                      d="M12 8v8m-4-4h8m5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                    />
                  </svg>
                  <span className="text-micro font-medium">Request</span>
                </button>
              </div>
            </div>
          </section>
        </Reveal>

        {/* PAYMENT LINKS */}
        <section>
          <h2 className="mb-2 font-semibold text-ink">Payment links</h2>
          <ul className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface shadow-card">
            {data.links.map((l) => (
              <li key={l.id} className="flex items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink">{l.title}</p>
                  <p className="text-xs text-ink-subtle">
                    {l.uses} payments &middot; {l.status}
                  </p>
                </div>
                <span className="tabular shrink-0 text-sm font-semibold text-ink">
                  KES {l.amount}
                </span>
              </li>
            ))}
          </ul>
        </section>

        {/* RECENT PAYMENTS */}
        <section>
          <h2 className="mb-2 font-semibold text-ink">Recent payments</h2>
          <ul className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface shadow-card">
            {data.payments.map((p) => (
              <li key={p.id} className="flex items-center gap-3 px-4 py-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent/12 text-accent">
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.8}
                      d="M18 6 6 18m0 0h8m-8 0v-8"
                    />
                  </svg>
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink">{p.from}</p>
                  <p className="text-xs text-ink-subtle capitalize">
                    {p.method} &middot; {fmtTime(p.at)}
                  </p>
                </div>
                <span className="tabular shrink-0 text-sm font-semibold text-accent">
                  + KES {p.amount}
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      {/* RAIL: settlement, now REAL (FR-U-TXN-006, FR-U-LQD-003).
          This replaced a fixture that showed a pending payout, a next-payout date and a disabled
          "coming soon" button. All three were invented: there was no settlement backend at all. The
          panel below prices both modes from the server and can actually move the money. */}
      <div className="space-y-6">
        <SettlementPanel asset="KES" />
      </div>

      {/* Show-QR sheet */}
      <Sheet open={qrOpen} onClose={() => setQrOpen(false)} title="Scan to pay">
        <div className="mx-auto w-fit rounded-xl bg-white p-4 shadow-soft">
          <QRCodeSVG
            value={`vasp:pay?till=${data.profile.till}`}
            size={200}
            fgColor={BRAND_PINE}
            bgColor={WHITE}
            level="M"
            marginSize={0}
          />
        </div>
        <p className="mt-3 text-center text-sm text-ink-muted">
          Till {data.profile.till} &middot; {data.profile.name}
        </p>
      </Sheet>

      {/* Create-link sheet */}
      <Sheet open={linkOpen} onClose={() => setLinkOpen(false)} title="New payment link">
        <div className="space-y-4">
          <div>
            <label htmlFor="lt" className="block text-sm font-medium text-ink">
              What is it for?
            </label>
            <input
              id="lt"
              placeholder="e.g. Deposit"
              className="mt-1 w-full rounded-field border border-border bg-surface px-3 py-2.5 text-base text-ink outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/25"
            />
          </div>
          <div>
            <label htmlFor="la" className="block text-sm font-medium text-ink">
              Amount (optional)
            </label>
            <div className="mt-1 flex items-center gap-2 rounded-field border border-border px-3 py-2.5 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/25">
              <span className="text-sm text-ink-muted">KES</span>
              <input
                id="la"
                inputMode="decimal"
                placeholder="0.00"
                className="w-full bg-transparent font-mono text-base text-ink outline-none"
              />
            </div>
          </div>
          <button
            onClick={() => setLinkOpen(false)}
            className="w-full rounded-field bg-primary py-2.5 text-sm font-semibold text-white transition hover:bg-primary-light"
          >
            Create link
          </button>
        </div>
      </Sheet>
    </div>
  );
}
